/**
 * Captures UTM parameters + referrer + landing path on first visit
 * and persists them in sessionStorage so the values survive in-app
 * navigation (e.g. /story → /guide).
 *
 * All values are defensively sanitized:
 *   - trimmed
 *   - control chars stripped
 *   - length-capped to fit the DB CHECK policy
 *   - empty/whitespace becomes null (never empty string)
 *   - non-string inputs become null
 *
 * Result: DB inserts and analytics events never break, even if a campaign
 * URL has malformed, missing, or absurdly long params.
 */

const KEY = "eliza_attribution_v1";

const FIELD_LIMITS = {
  utm_source: 100,
  utm_medium: 100,
  utm_campaign: 150,
  utm_term: 150,
  utm_content: 150,
  referrer: 500,
  landing_path: 500,
} as const;

type Field = keyof typeof FIELD_LIMITS;

export interface Attribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_path: string | null;
}

const EMPTY: Attribution = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null,
  referrer: null,
  landing_path: null,
};

/**
 * Normalize any unknown value to a safe string or null:
 * - non-strings → null
 * - strip control chars (incl. nulls, newlines)
 * - collapse internal whitespace
 * - trim
 * - cap length
 * - empty after cleaning → null
 */
const sanitize = (value: unknown, max: number): string | null => {
  if (typeof value !== "string") return null;
  // Remove control chars (0x00-0x1F, 0x7F) and zero-width chars
  // eslint-disable-next-line no-control-regex
  const cleaned = value
    .replace(/[\u0000-\u001F\u007F\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return null;
  return cleaned.slice(0, max);
};

const sameOrigin = (url: string): boolean => {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
};

const safeParam = (params: URLSearchParams, key: Field): string | null => {
  try {
    return sanitize(params.get(key), FIELD_LIMITS[key]);
  } catch {
    return null;
  }
};

const read = (): Attribution | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Attribution>;
    if (!parsed || typeof parsed !== "object") return null;
    // Re-sanitize on read, in case stored data was tampered with.
    return {
      utm_source: sanitize(parsed.utm_source, FIELD_LIMITS.utm_source),
      utm_medium: sanitize(parsed.utm_medium, FIELD_LIMITS.utm_medium),
      utm_campaign: sanitize(parsed.utm_campaign, FIELD_LIMITS.utm_campaign),
      utm_term: sanitize(parsed.utm_term, FIELD_LIMITS.utm_term),
      utm_content: sanitize(parsed.utm_content, FIELD_LIMITS.utm_content),
      referrer: sanitize(parsed.referrer, FIELD_LIMITS.referrer),
      landing_path: sanitize(parsed.landing_path, FIELD_LIMITS.landing_path),
    };
  } catch {
    return null;
  }
};

const write = (a: Attribution) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    /* ignore quota / private mode */
  }
};

/**
 * Captures attribution from the current URL + document.referrer once per session.
 * Safe to call on every page load — it only writes the first time.
 * Always returns a fully-shaped object; never throws.
 */
export const captureAttribution = (): Attribution => {
  if (typeof window === "undefined") return { ...EMPTY };

  try {
    const existing = read();
    if (existing) return existing;

    let params: URLSearchParams;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      params = new URLSearchParams();
    }

    const ref =
      typeof document !== "undefined" &&
      typeof document.referrer === "string" &&
      document.referrer &&
      !sameOrigin(document.referrer)
        ? document.referrer
        : null;

    const landing = `${window.location.pathname ?? ""}${window.location.search ?? ""}`;

    const attribution: Attribution = {
      utm_source: safeParam(params, "utm_source"),
      utm_medium: safeParam(params, "utm_medium"),
      utm_campaign: safeParam(params, "utm_campaign"),
      utm_term: safeParam(params, "utm_term"),
      utm_content: safeParam(params, "utm_content"),
      referrer: sanitize(ref, FIELD_LIMITS.referrer),
      landing_path: sanitize(landing, FIELD_LIMITS.landing_path),
    };

    write(attribution);
    return attribution;
  } catch {
    return { ...EMPTY };
  }
};

/** Returns the captured attribution (or captures it now if missing). Never throws. */
export const getAttribution = (): Attribution => {
  try {
    return read() ?? captureAttribution();
  } catch {
    return { ...EMPTY };
  }
};
